import { alpha, Box, Divider, Typography } from '@mui/material'
import React from 'react'
import { SingleItemCard } from './SingleItemCard'
import { VariantGroupCard } from './VariantGroupCard'
import { SERVICE_LINE_COLORS, SERVICE_LINE_ICONS } from './icons'
import { CatalogItem, getPhaseShortLabel, Phase, RoadmapState } from './types'
import { ItemGroup } from './variantGrouping'

interface CatalogGridProps {
  state: RoadmapState
  grouped: {
    code: string
    label: string
    variantGroups: ItemGroup[]
    selectedGroups: ItemGroup[]
    availableGroups: ItemGroup[]
  }[]
  activePhaseItemIds: Set<string>
  getItemPhases: (productId: string) => Set<Phase>
  removeFromAll: (productId: string) => void
  addItem: (item: CatalogItem, phase?: Phase) => void
  handleOpenMultiMonth: (event: React.MouseEvent<HTMLElement, MouseEvent>, item: CatalogItem) => void
  effectiveRate: number
  isSetupPhase: boolean
}

const CatalogGrid: React.FC<CatalogGridProps> = ({
  state,
  grouped,
  activePhaseItemIds,
  getItemPhases,
  removeFromAll,
  addItem,
  handleOpenMultiMonth,
  effectiveRate,
  isSetupPhase,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {grouped.map(group => {
        const slColor = SERVICE_LINE_COLORS[group.code] || '#888'
        const SlIcon = SERVICE_LINE_ICONS[group.code]

        return (
          <Box key={group.code}>
            {/* Service line header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              {SlIcon && <SlIcon sx={{ fontSize: 16, color: slColor }} />}
              <Typography
                variant="overline"
                sx={{ color: slColor, fontWeight: 700, letterSpacing: 1.2, lineHeight: 1 }}
              >
                {group.label} ({group.variantGroups.length})
              </Typography>
            </Box>

            {/* Selected items for this phase */}
            {group.selectedGroups.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'success.main',
                    fontWeight: 600,
                    fontSize: 10,
                    letterSpacing: 0.5,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Added to {getPhaseShortLabel(state.activePhase)} ({group.selectedGroups.length})
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  {group.selectedGroups.map(vg =>
                    vg.isVariantGroup ? (
                      <VariantGroupCard
                        key={`vg-${vg.baseName}-${vg.serviceLine}`}
                        group={vg}
                        activePhaseItemIds={activePhaseItemIds}
                        getItemPhases={getItemPhases}
                        removeFromAll={removeFromAll}
                        addItem={addItem}
                        handleOpenMultiMonth={handleOpenMultiMonth}
                        effectiveRate={effectiveRate}
                        slColor={slColor}
                        SlIcon={SlIcon}
                        groupLabel={group.label}
                        isSetupPhase={isSetupPhase}
                        activePhase={state.activePhase}
                        recommendedSkus={state.recommendedSkus}
                      />
                    ) : (
                      <SingleItemCard
                        key={vg.items[0].products_uuid}
                        item={vg.items[0]}
                        activePhaseItemIds={activePhaseItemIds}
                        getItemPhases={getItemPhases}
                        removeFromAll={removeFromAll}
                        addItem={addItem}
                        handleOpenMultiMonth={handleOpenMultiMonth}
                        effectiveRate={effectiveRate}
                        slColor={slColor}
                        SlIcon={SlIcon}
                        groupLabel={group.label}
                        isSetupPhase={isSetupPhase}
                        activePhase={state.activePhase}
                        recommendedSkus={state.recommendedSkus}
                      />
                    )
                  )}
                </Box>
                {group.availableGroups.length > 0 && <Divider sx={{ my: 1, borderColor: alpha(slColor, 0.2) }} />}
              </>
            )}

            {/* Available items */}
            {group.availableGroups.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 1.5,
                }}
              >
                {group.availableGroups.map(vg =>
                  vg.isVariantGroup ? (
                    <VariantGroupCard
                      key={`vg-${vg.baseName}-${vg.serviceLine}`}
                      group={vg}
                      activePhaseItemIds={activePhaseItemIds}
                      getItemPhases={getItemPhases}
                      removeFromAll={removeFromAll}
                      addItem={addItem}
                      handleOpenMultiMonth={handleOpenMultiMonth}
                      effectiveRate={effectiveRate}
                      slColor={slColor}
                      SlIcon={SlIcon}
                      groupLabel={group.label}
                      isSetupPhase={isSetupPhase}
                      activePhase={state.activePhase}
                      recommendedSkus={state.recommendedSkus}
                    />
                  ) : (
                    <SingleItemCard
                      key={vg.items[0].products_uuid}
                      item={vg.items[0]}
                      activePhaseItemIds={activePhaseItemIds}
                      getItemPhases={getItemPhases}
                      removeFromAll={removeFromAll}
                      addItem={addItem}
                      handleOpenMultiMonth={handleOpenMultiMonth}
                      effectiveRate={effectiveRate}
                      slColor={slColor}
                      SlIcon={SlIcon}
                      groupLabel={group.label}
                      isSetupPhase={isSetupPhase}
                      activePhase={state.activePhase}
                      recommendedSkus={state.recommendedSkus}
                    />
                  )
                )}
              </Box>
            )}
          </Box>
        )
      })}

      {grouped.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="body2">No services match your search or filters.</Typography>
        </Box>
      )}
    </Box>
  )
}

export default CatalogGrid
